import { useEffect, useState } from 'react';
import { Button, Space } from 'antd';
import { PictureOutlined, EditOutlined, CloseOutlined } from '@ant-design/icons';
import request from '@/utils/request';
import { thumb } from '@/utils/image';
import ImagePicker from './index';

interface Props {
  value: number | null | undefined;
  onChange: (imageId: number | null) => void;
  disabled?: boolean;
  placeholder?: string;
  /** Ratio for the preview tile. Defaults to 1. */
  aspectRatio?: number;
  /** Height of the preview tile in pixels when no aspectRatio is provided. */
  height?: number;
}

const imageUrlCache = new Map<number, string>();

async function fetchImageUrl(imageId: number): Promise<string | null> {
  if (imageUrlCache.has(imageId)) return imageUrlCache.get(imageId)!;
  try {
    const res = await request.get<{ items: { id: number; accessUrl: string }[] }>(
      `/creator/images?ids=${imageId}`,
    );
    const url = (res.data as unknown as { items: { id: number; accessUrl: string }[] }).items[0]?.accessUrl;
    if (url) {
      imageUrlCache.set(imageId, url);
      return url;
    }
    return null;
  } catch {
    return null;
  }
}

const ImagePickerField: React.FC<Props> = ({
  value,
  onChange,
  disabled,
  placeholder = '选择图片',
  aspectRatio = 1,
  height,
}) => {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!value) {
      setUrl(null);
      return;
    }
    let alive = true;
    fetchImageUrl(value).then((u) => {
      if (alive) setUrl(u);
    });
    return () => {
      alive = false;
    };
  }, [value]);

  const tileStyle: React.CSSProperties = {
    width: '100%',
    ...(height ? { height } : { aspectRatio: String(aspectRatio) }),
    borderRadius: 10,
    overflow: 'hidden',
    border: '1.5px dashed #E7E2D6',
    background: '#F6F3EC',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#A79672',
    flexDirection: 'column',
    gap: 6,
    cursor: disabled ? 'not-allowed' : 'pointer',
    position: 'relative',
  };

  return (
    <>
      {url ? (
        <div style={{ ...tileStyle, border: '1px solid #E7E2D6', background: '#fff' }}>
          <img
            src={thumb(url, height ?? 200)}
            alt=""
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
          {!disabled && (
            <div style={{ position: 'absolute', top: 6, right: 6, display: 'flex', gap: 4 }}>
              <Button
                size="small"
                shape="circle"
                icon={<EditOutlined />}
                onClick={() => setOpen(true)}
              />
              <Button
                size="small"
                shape="circle"
                danger
                icon={<CloseOutlined />}
                onClick={() => onChange(null)}
              />
            </div>
          )}
        </div>
      ) : (
        <div
          style={tileStyle}
          onClick={() => {
            if (!disabled) setOpen(true);
          }}
        >
          <PictureOutlined style={{ fontSize: 28 }} />
          <span style={{ fontSize: 13 }}>{placeholder}</span>
        </div>
      )}
      <ImagePicker
        open={open}
        mode="single"
        onClose={() => setOpen(false)}
        onPick={(ids) => {
          onChange(ids[0] ?? null);
          setOpen(false);
        }}
      />
      <Space style={{ marginTop: 6 }} size={4}>
        {url && !disabled && (
          <a style={{ fontSize: 12 }} onClick={() => setOpen(true)}>
            更换
          </a>
        )}
      </Space>
    </>
  );
};

export default ImagePickerField;
